/**
 * LayoutRenderer Component
 *
 * Recursive SplitPane tree renderer — desktop: nested SplitPanes from
 * split tree definition; mobile: CSS grid (1-column stacked or 2-column
 * quadrant per preset `mobileLayout` field).
 *
 * Renders FeaturePaneShell at each leaf position, displaying the
 * assigned feature's identity (icon, label) and placeholder content.
 *
 * Supports per-pane expand/focus mode: when `expandedSlotId` is set,
 * `forcedSize` is applied along the split-tree path to the target slot,
 * giving it full layout space while sibling panes collapse to 0px but
 * remain mounted (preserving transient state). On mobile, the expanded
 * pane gets full viewport while siblings are hidden via CSS.
 *
 * @see types/shared/Layout.ts for SplitNodeDefinition, LayoutPresetConfig
 * @see constants/layout.ts for preset definitions
 * @see components/shared/SplitPane for SplitPane component
 * @see ./FeaturePaneShell.tsx for pane shell component
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { SplitPane } from '@/components/shared/SplitPane';
import { MIN_PANE_PX_FLOOR, MIN_PANE_RATIO, MAX_PANE_PERCENT } from '@/constants/layout';
import { useIsDesktop } from '@/hooks/useIsDesktop';

import { FeaturePaneShell } from './FeaturePaneShell';

import type { FeatureAreaId, LayoutPresetConfig, SlotId, SplitNodeDefinition, SplitPointId } from '@/types/shared';

// ===== HELPERS =====

/**
 * Compute new slot assignments after a swap operation.
 * Enforces uniqueness: clears ALL existing occurrences of the selected feature
 * before assigning, handling both clean and dirty (duplicate) persisted data.
 *
 * - If selectedFeatureId is on another slot: two-way swap (displaced ↔ selected)
 * - If selectedFeatureId is unassigned: one-way assignment (displaced becomes overflow)
 */
function computeSwapAssignments(
  current: Partial<Record<SlotId, FeatureAreaId>>,
  fromSlotId: SlotId,
  selectedFeatureId: FeatureAreaId,
): Partial<Record<SlotId, FeatureAreaId>> {
  const next = { ...current };
  const displacedFeature = next[fromSlotId];

  // Clear ALL slots holding the selected feature (uniqueness enforcement).
  // First source slot gets the displaced feature (two-way swap);
  // any additional duplicates (dirty data) are cleared entirely.
  let swapped = false;
  for (const [slotId, featureId] of Object.entries(next) as [SlotId, FeatureAreaId][]) {
    if (featureId === selectedFeatureId && slotId !== fromSlotId) {
      if (!swapped && displacedFeature !== undefined) {
        next[slotId] = displacedFeature;
        swapped = true;
      } else {
        delete next[slotId];
      }
    }
  }

  // Assign selected feature to target slot
  next[fromSlotId] = selectedFeatureId;
  return next;
}

/**
 * Trace the path from root to a target slot through the split tree.
 * Returns a Map of splitPointId → 'first' | 'second' for each split node
 * on the path, or null if the target slot is not found.
 */
function findExpandPath(
  node: SplitNodeDefinition,
  targetSlotId: SlotId,
): Map<SplitPointId, 'first' | 'second'> | null {
  if (node.type === 'slot') {
    return node.slotId === targetSlotId ? new Map() : null;
  }

  const firstResult = findExpandPath(node.first, targetSlotId);
  if (firstResult !== null) {
    firstResult.set(node.splitPointId, 'first');
    return firstResult;
  }

  const secondResult = findExpandPath(node.second, targetSlotId);
  if (secondResult !== null) {
    secondResult.set(node.splitPointId, 'second');
    return secondResult;
  }

  return null;
}

// ===== CONSTANTS =====

/**
 * Total space occupied by SplitPane's divider hit area (px).
 * Must match SplitPane's internal DIVIDER_FOOTPRINT constant.
 */
const DIVIDER_FOOTPRINT = 12;

// ===== TYPES =====

export interface LayoutRendererProps {
  presetConfig: LayoutPresetConfig;
  paneWeights: Partial<Record<SplitPointId, number>> | undefined;
  slotAssignments: Partial<Record<SlotId, FeatureAreaId>> | undefined;
  onWeightChange: (splitPointId: SplitPointId, weight: number) => void;
  onSlotAssignmentChange: (slotAssignments: Partial<Record<SlotId, FeatureAreaId>>) => void;
  /** Feature body content + header actions per feature area (WU2–WU6) */
  featureContentMap?: Partial<Record<FeatureAreaId, { content?: React.ReactNode; actions?: React.ReactNode }>>;
  /** Slot ID of the currently expanded pane (undefined = no pane expanded) */
  expandedSlotId?: SlotId;
  /** Callback to toggle expand/collapse on a slot */
  onExpandToggle?: (slotId: SlotId) => void;
}

// ===== COMPONENT =====

export const LayoutRenderer: React.FC<LayoutRendererProps> = React.memo(({
  presetConfig,
  paneWeights,
  slotAssignments,
  onWeightChange,
  onSlotAssignmentChange,
  featureContentMap,
  expandedSlotId,
  onExpandToggle,
}) => {
  const isDesktop = useIsDesktop();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null);

  // ---------------------------------------------------------------------------
  // Stable swap callback via refs — prevents React.memo defeat on FeaturePaneShell
  // ---------------------------------------------------------------------------

  const slotAssignmentsRef = useRef(slotAssignments);
  slotAssignmentsRef.current = slotAssignments;

  const onSlotAssignmentChangeRef = useRef(onSlotAssignmentChange);
  onSlotAssignmentChangeRef.current = onSlotAssignmentChange;

  const handlePaneSwap = useCallback(
    (fromSlotId: SlotId, selectedFeatureId: FeatureAreaId): void => {
      const current = slotAssignmentsRef.current;
      const onChange = onSlotAssignmentChangeRef.current;
      if (!current || !onChange) return;
      onChange(computeSwapAssignments(current, fromSlotId, selectedFeatureId));
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Expand path — traces root → target slot through the split tree
  // ---------------------------------------------------------------------------

  // Validate expandedSlotId against current preset's slots — prevents stale
  // expanded state (e.g., during preset change) from hiding all mobile panes
  const effectiveExpandedSlotId = expandedSlotId !== undefined
    && (presetConfig.slotIds as readonly string[]).includes(expandedSlotId)
    ? expandedSlotId
    : undefined;

  const expandPath = useMemo(
    () => effectiveExpandedSlotId !== undefined
      ? findExpandPath(presetConfig.splitTree, effectiveExpandedSlotId)
      : null,
    [effectiveExpandedSlotId, presetConfig.splitTree],
  );

  // ---------------------------------------------------------------------------
  // Stable expand toggle callback via ref — prevents React.memo defeat
  // ---------------------------------------------------------------------------

  const onExpandToggleRef = useRef(onExpandToggle);
  onExpandToggleRef.current = onExpandToggle;

  const stableExpandToggle = useCallback(
    (slotId: SlotId): void => { onExpandToggleRef.current?.(slotId); },
    [],
  );

  // ---------------------------------------------------------------------------
  // Container dimension measurement via ResizeObserver
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isDesktop) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setContainerSize({ width, height });
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [isDesktop]);

  // ---------------------------------------------------------------------------
  // Weight lookup helper
  // ---------------------------------------------------------------------------

  const getWeight = useCallback(
    (splitPointId: SplitPointId): number => {
      return paneWeights?.[splitPointId]
        ?? presetConfig.defaultWeights[splitPointId]
        ?? 0.5;
    },
    [paneWeights, presetConfig.defaultWeights],
  );

  // ---------------------------------------------------------------------------
  // Recursive renderNode
  // ---------------------------------------------------------------------------

  const renderNode = useCallback(
    (node: SplitNodeDefinition, availableWidth: number, availableHeight: number): React.ReactNode => {
      if (node.type === 'slot') {
        const fid = slotAssignments?.[node.slotId];
        const fc = fid !== undefined ? featureContentMap?.[fid] : undefined;
        return (
          <FeaturePaneShell
            slotId={node.slotId}
            featureAreaId={fid}
            isDesktop={isDesktop}
            onSwap={handlePaneSwap}
            actions={fc?.actions}
            isExpanded={node.slotId === effectiveExpandedSlotId}
            {...(onExpandToggle ? { onExpandToggle: stableExpandToggle } : {})}
          >
            {fc?.content}
          </FeaturePaneShell>
        );
      }

      // Split node — compute defaultSize and minSize from available dimensions for this subtree
      const dimension = node.orientation === 'vertical' ? availableHeight : availableWidth;
      const weight = getWeight(node.splitPointId);
      const defaultSize = Math.round(weight * dimension);
      // Reset target uses preset default weight (not persisted weight) so double-click always returns to baseline
      const presetDefaultWeight = presetConfig.defaultWeights[node.splitPointId] ?? 0.5;
      const resetSize = Math.round(presetDefaultWeight * dimension);
      // Symmetrical min: ratio of split-axis dimension or pixel floor (prevents collapse on small containers)
      const minSize = Math.max(MIN_PANE_PX_FLOOR, Math.round(MIN_PANE_RATIO * dimension));

      // Expand path — if this split is on the path, force sizes to give full space to the target branch
      const expandDirection = expandPath?.get(node.splitPointId);

      if (expandDirection !== undefined) {
        // Forced mode: no divider rendered → full dimension available to one branch
        const forcedSize = expandDirection === 'first' ? dimension : 0;
        const firstDim = forcedSize;
        const secondDim = dimension - forcedSize;

        let firstChildWidth: number;
        let firstChildHeight: number;
        let secondChildWidth: number;
        let secondChildHeight: number;

        if (node.orientation === 'vertical') {
          firstChildWidth = availableWidth;
          firstChildHeight = firstDim;
          secondChildWidth = availableWidth;
          secondChildHeight = secondDim;
        } else {
          firstChildWidth = firstDim;
          firstChildHeight = availableHeight;
          secondChildWidth = secondDim;
          secondChildHeight = availableHeight;
        }

        return (
          <SplitPane
            key={node.splitPointId}
            orientation={node.orientation}
            defaultSize={defaultSize}
            resetSize={resetSize}
            firstContent={renderNode(node.first, firstChildWidth, firstChildHeight)}
            secondContent={renderNode(node.second, secondChildWidth, secondChildHeight)}
            onResize={(newSize, containerDim) => onWeightChange(node.splitPointId, newSize / containerDim)}
            minSize={minSize}
            maxSize={MAX_PANE_PERCENT}
            forcedSize={forcedSize}
          />
        );
      }

      // Normal mode — no expand path through this split
      const remainingDimension = Math.max(0, dimension - defaultSize - DIVIDER_FOOTPRINT);

      let firstChildWidth: number;
      let firstChildHeight: number;
      let secondChildWidth: number;
      let secondChildHeight: number;

      if (node.orientation === 'vertical') {
        // Vertical split: height changes, width passes through
        firstChildWidth = availableWidth;
        firstChildHeight = defaultSize;
        secondChildWidth = availableWidth;
        secondChildHeight = remainingDimension;
      } else {
        // Horizontal split: width changes, height passes through
        firstChildWidth = defaultSize;
        firstChildHeight = availableHeight;
        secondChildWidth = remainingDimension;
        secondChildHeight = availableHeight;
      }

      return (
        <SplitPane
          key={node.splitPointId}
          orientation={node.orientation}
          defaultSize={defaultSize}
          resetSize={resetSize}
          firstContent={renderNode(node.first, firstChildWidth, firstChildHeight)}
          secondContent={renderNode(node.second, secondChildWidth, secondChildHeight)}
          onResize={(newSize, containerDim) => onWeightChange(node.splitPointId, newSize / containerDim)}
          minSize={minSize}
          maxSize={MAX_PANE_PERCENT}
        />
      );
    },
    [effectiveExpandedSlotId, expandPath, featureContentMap, getWeight, handlePaneSwap, isDesktop, onExpandToggle, onWeightChange, presetConfig.defaultWeights, slotAssignments, stableExpandToggle],
  );

  // ---------------------------------------------------------------------------
  // Mobile: CSS grid (1-column stacked or 2-column quadrant per preset)
  // ---------------------------------------------------------------------------

  if (!isDesktop) {
    const isAnyExpanded = effectiveExpandedSlotId != null;
    const isStacked = presetConfig.mobileLayout === 'stacked';
    const gridColsClass = isStacked ? 'grid-cols-1' : 'grid-cols-2';

    return (
      <div className={isAnyExpanded ? 'h-full w-full' : `grid ${gridColsClass} auto-rows-fr h-full gap-px bg-default-200`}>
        {presetConfig.slotIds.map((slotId) => {
          const fid = slotAssignments?.[slotId];
          const fc = fid !== undefined ? featureContentMap?.[fid] : undefined;
          const isThisExpanded = slotId === effectiveExpandedSlotId;

          let wrapperClass: string;
          if (isAnyExpanded) {
            wrapperClass = isThisExpanded ? 'h-full w-full' : 'hidden';
          } else {
            wrapperClass = `min-h-0 overflow-hidden ${!isStacked && slotId === presetConfig.mobileSpanningSlot ? 'col-span-2' : ''}`;
          }

          return (
            <div key={slotId} className={wrapperClass}>
              <FeaturePaneShell
                slotId={slotId}
                featureAreaId={fid}
                isDesktop={false}
                onSwap={handlePaneSwap}
                actions={fc?.actions}
                isExpanded={isThisExpanded}
                {...(onExpandToggle ? { onExpandToggle: stableExpandToggle } : {})}
              >
                {fc?.content}
              </FeaturePaneShell>
            </div>
          );
        })}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Desktop: Recursive SplitPane tree
  // ---------------------------------------------------------------------------

  return (
    <div ref={containerRef} className="h-full w-full">
      {containerSize && renderNode(
        presetConfig.splitTree,
        containerSize.width,
        containerSize.height,
      )}
    </div>
  );
});

LayoutRenderer.displayName = 'LayoutRenderer';
